import { type RPPDomain } from '@retailer-platform/dashboard/core'
import { NavEntryPoint, NavEntryPointV3 } from '@retailer-platform/dashboard/routing'
import { productsRouter } from './routing/productsRouter'
import { type DomainMessages } from './utils/domain/intl'
import { routes } from './routing/routes'
import EN_US from './translations/en-US.json'
import { type DomainAccessControlConfig } from './utils/domain/accessControl'
import { productsSearchAccessControl } from './access-control/searchAccess.configuration'
import {
  productsViewMissingCodesAccessControl,
  productsViewLowPriceAlertsAccessControl,
  aiProductCreatorAccessControl,
} from './access-control/viewAccess.configuration.ts'
import { Permission } from './access-control/permissions'

const definition: RPPDomain<typeof routes, DomainMessages, DomainAccessControlConfig> = {
  id: 'products',
  totem: {
    // The default IPP tool of all routes in this domain - should match the Navigation Entry in IPP UI.
    // Individual routes can override this by specifying a `tool` prop on their DomainRoute component
    tool: 'products-dashboard-tool',
  },
  routes,
  translations: { EN_US },
  lazyTranslations: {
    ES_ES: () => import('./translations/es-ES.json'),
  },
  routers: {
    scoped: [productsRouter],
  },
  navigationEntries: [
    {
      attachToV2: NavEntryPoint.Catalog,
      attachToV3: NavEntryPointV3.Catalog,
      subNavItems: [
        {
          type: 'item',
          llmDescription:
            'Search and edit product information, including pricing, attributes, and availability in the catalog',
          labelId: 'productsDomain.search.title',
          labelIdV3: 'productsDomain.search.title.v3',
          route: 'products-search',
          subRoutes: [
            'products-view',
            'products-view-details',
            'products-view-store-locations',
            'products-view-aisles',
            'products-view-attribute-overrides',
            'products-view-collections',
            'products-edit-images',
          ],
          accessControl: productsSearchAccessControl,
          positionNavV2: 0,
          positionNavV3: 0,
          inToolPermissions: [
            {
              slug: Permission.ProductsCatalogProductsModify,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.ProductsCatalogProductsModify,
            },
            {
              slug: Permission.CatalogProductAttributesView,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.CatalogProductAttributesView,
            },
            {
              slug: Permission.ProductAttributesEdit,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.ProductAttributesEdit,
            },
            {
              slug: Permission.ProductsRetailerCollectionsV2,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.ProductsRetailerCollectionsV2,
            },
          ],
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.Catalog,
      attachToV3: NavEntryPointV3.Catalog,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'View and manage products with missing barcodes or UPC codes',
          labelId: 'productsDomain.missingCodes.title',
          route: 'products-missing-codes',
          subRoutes: ['products-missing-codes-details'],
          accessControl: productsViewMissingCodesAccessControl,
          positionNavV2: 7,
          positionNavV3: 8,
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.Catalog,
      attachToV3: NavEntryPointV3.Catalog,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'Monitor and manage low-priced product alerts',
          labelId: 'productsDomain.lowPriceAlert.title',
          labelIdV3: 'productsDomain.lowPriceAlert.title.v3',
          route: 'products-low-price-alerts',
          accessControl: productsViewLowPriceAlertsAccessControl,
          positionNavV2: 8,
          positionNavV3: 10,
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.Catalog,
      attachToV3: NavEntryPointV3.Catalog,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'Create product listings using AI-powered image analysis',
          labelId: 'productsDomain.aiProductCreator.title',
          route: 'products-ai-product-creator',
          accessControl: aiProductCreatorAccessControl,
          positionNavV2: 10,
          positionNavV3: 11,
        },
      ],
    },
  ],
  adminNavigationEntries: [],
}

export default definition
